import { DaySchedule } from 'components/CreateBooking/Daycare/FeedingsAndMedications/UI/MedicalSchedule/MedicalSchedule';
import { DataVaraible, Feeding, Medication } from 'model/NewMultipleBooking';
import { Moment } from 'moment-timezone';
import { Task } from '@spike/task-model';
import {
    createMedicationTaskDate,
    createTaskDate,
    getRangeDateByMomentTypeIdWithTimezone,
    shouldCreateTaskForDay
} from 'utils/dateTimeUtils';
import { FeedingSlotTimes } from 'utils/feedingSlotTimes';

export const getDaycareAppointmentTasksToSave = (
    variableData: DataVaraible,
    roomUnitId: number | undefined,
    timezone: string,
    feedingSlots: FeedingSlotTimes
): Array<Task> => {
    const items: Array<Task> = [];
    const { service, date, dateEnd, itemData } = variableData;
    const flow = itemData?.flow;
    const { morning, afternoon, evening } = feedingSlots;

    const isMultiDay = flow === 'boarding' && dateEnd && dateEnd.isAfter(date);

    const createFeedingTasksForDay = (dayLocal: Moment) => {
        service?.feedings?.forEach((feeding: Feeding) => {
            const legacySpecificOnly =
                (!feeding.momentTypes || feeding.momentTypes.length === 0) &&
                !!feeding.specificMomentTimes &&
                Object.keys(feeding.specificMomentTimes).length > 0;

            const pushPresetForMoment = (momentTypeId: number) => {
                const taskDate = createTaskDate(
                    dayLocal,
                    momentTypeId === 1 ? morning : momentTypeId === 2 ? afternoon : evening,
                    timezone
                );
                const rangeDate = getRangeDateByMomentTypeIdWithTimezone(
                    dayLocal,
                    momentTypeId,
                    morning,
                    afternoon,
                    evening,
                    timezone
                );

                if (isMultiDay) {
                    if (!shouldCreateTaskForDay(taskDate, date!, dateEnd!, rangeDate.from, rangeDate.to)) {
                        return;
                    }
                }

                items.push({
                    taskDate,
                    feeding: {
                        taskType: 1,
                        feedingMomentTypeId: momentTypeId,
                        foodProvisionerTypeId: feeding.foodProvisionType,
                        foodTypeId: feeding.foodType,
                        foodQuantity: feeding.quantity?.quantity?.toString(),
                        foodQuantityUnitId: feeding.quantity?.unit,
                        feedingInstructions: feeding.instructions,
                        feedingBrand: feeding.brand,
                        roomUnitId,
                        specificTime: false
                    }
                } as Task);
            };

            if (feeding.feedingScheduleType === 'specificTime' && feeding.momentTypes?.length) {
                const anySelectedHasTime = feeding.momentTypes.some(
                    m => !!String(feeding.specificMomentTimes?.[m] ?? '').trim()
                );
                if (anySelectedHasTime) {
                    feeding.momentTypes.forEach((momentTypeId: number) => {
                        const timeString = feeding.specificMomentTimes?.[momentTypeId];
                        if (!timeString || !String(timeString).trim()) return;
                        const taskDate = createMedicationTaskDate(dayLocal, String(timeString).trim(), timezone);

                        if (isMultiDay) {
                            if (taskDate.isBefore(date) || taskDate.isSameOrAfter(dateEnd!)) {
                                return;
                            }
                        }

                        items.push({
                            taskDate,
                            feeding: {
                                taskType: 1,
                                feedingMomentTypeId: momentTypeId,
                                foodProvisionerTypeId: feeding.foodProvisionType,
                                foodTypeId: feeding.foodType,
                                foodQuantity: feeding.quantity?.quantity?.toString(),
                                foodQuantityUnitId: feeding.quantity?.unit,
                                feedingInstructions: feeding.instructions,
                                feedingBrand: feeding.brand,
                                roomUnitId,
                                specificTime: true
                            }
                        } as Task);
                    });
                } else {
                    feeding.momentTypes.forEach(pushPresetForMoment);
                }
            } else if (legacySpecificOnly) {
                Object.entries(feeding.specificMomentTimes!).forEach(([momentTypeIdStr, timeString]) => {
                    const momentTypeId = Number(momentTypeIdStr);
                    if (!timeString || !String(timeString).trim()) return;
                    const taskDate = createMedicationTaskDate(dayLocal, String(timeString).trim(), timezone);

                    if (isMultiDay) {
                        if (taskDate.isBefore(date) || taskDate.isSameOrAfter(dateEnd!)) {
                            return;
                        }
                    }

                    items.push({
                        taskDate,
                        feeding: {
                            taskType: 1,
                            feedingMomentTypeId: momentTypeId,
                            foodProvisionerTypeId: feeding.foodProvisionType,
                            foodTypeId: feeding.foodType,
                            foodQuantity: feeding.quantity?.quantity?.toString(),
                            foodQuantityUnitId: feeding.quantity?.unit,
                            feedingInstructions: feeding.instructions,
                            feedingBrand: feeding.brand,
                            roomUnitId,
                            specificTime: true
                        }
                    } as Task);
                });
            } else {
                feeding.momentTypes?.forEach(pushPresetForMoment);
            }
        });
    };

    const createMedicationTasksForDay = (dayLocal: Moment) => {
        service?.medications?.forEach((medication: Medication) => {
            medication.schedule?.forEach((scheduleTime: string) => {
                const taskDate = createMedicationTaskDate(dayLocal, scheduleTime, timezone);

                if (isMultiDay) {
                    if (taskDate.isBefore(date) || taskDate.isSameOrAfter(dateEnd!)) {
                        return;
                    }
                }

                items.push({
                    taskDate,
                    medication: {
                        taskType: 2,
                        medicationName: medication.name,
                        medicationMethodId: medication.methodType,
                        dosageAmount: medication.dosage?.quantity?.toString(),
                        dosageUnitId: medication.dosage?.unit,
                        medicationInstructions: medication.instructions,
                        roomUnitId
                    }
                });
            });
        });
    };

    const createSpecificMedicationTasks = () => {
        service?.medications?.forEach((medication: Medication) => {
            medication.specificSchedule?.forEach((scheduleTime: DaySchedule) => {
                if (scheduleTime.enabled) {
                    scheduleTime.doses?.forEach((dose: string) => {
                        const taskDate = createMedicationTaskDate(scheduleTime.date, dose, timezone);

                        items.push({
                            taskDate,
                            medication: {
                                taskType: 2,
                                medicationName: medication.name,
                                medicationMethodId: medication.methodType,
                                dosageAmount: medication.dosage?.quantity?.toString(),
                                dosageUnitId: medication.dosage?.unit,
                                medicationInstructions: medication.instructions,
                                roomUnitId
                            }
                        });
                    });
                }
            });
        });
    };

    if (isMultiDay) {
        const start = date!.clone().tz(timezone).startOf('day');
        const end = dateEnd!.clone().tz(timezone).startOf('day');
        const totalDays = end.diff(start, 'days');

        for (let i = 0; i <= totalDays; i++) {
            const currentLocal = start.clone().add(i, 'days');
            createFeedingTasksForDay(currentLocal);
            createMedicationTasksForDay(currentLocal);
        }
        createSpecificMedicationTasks();
    } else {
        const dayLocal = date!.clone().tz(timezone);
        createFeedingTasksForDay(dayLocal);
        createMedicationTasksForDay(dayLocal);
    }

    return items;
};
